/* eslint-disable @typescript-eslint/ban-ts-comment */

import React, {
  useMemo,
  createContext,
  useContext,
  useCallback,
  useState,
} from "react";
import {
  AmbientLight,
  DirectionalLight,
  DoubleSide,
  Group,
  PointLight,
  Vector2,
  Texture,
  Mesh,
  Material,
  WebGLRenderer,
  Object3D,
} from "three";
import { MTLLoader } from "../MTLLoader";
import OBJLoader from "../OBJLoader";
import { ModelData, Point, RegionData } from "../../../../../types";
import useModelViewer from "./useModelViewer";

interface ModelViewerProvider {
  loaded: boolean;
  getWidthHeightOfElement: (
    element: React.MutableRefObject<HTMLDivElement>,
  ) => Vector2;
  setupCameraAndLights: (
    container: React.MutableRefObject<HTMLDivElement>,
    cameraPosition: Point,
  ) => void;
  constrainToBoundaries: (
    value: number,
    upper: number,
    lower: number,
  ) => number;
  setRenderSizeFromVector: (
    renderer: WebGLRenderer,
    dimensions: Vector2,
  ) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  notifySubscribers: (event: any) => any;
  setupManager: () => void;
  loadProduct: (modelData: ModelData) => void;
  setupControls: () => void;
  handleResize: (containerRef: React.MutableRefObject<HTMLDivElement>) => void;
  setupEventListeners: (
    containerRef: React.MutableRefObject<HTMLDivElement>,
  ) => void;
  initScene: (
    modelData: ModelData,
    containerRef: React.MutableRefObject<HTMLDivElement>,
    cameraPosition: Point,
    regionData: RegionData,
  ) => void;
  beginRender: () => void;
  setTexture: (
    object: Group,
    texture: Texture,
    textureOriginalUrl: string,
    isBump: boolean,
    isAlpha: boolean,
  ) => void;
  loadTextureFromCanvas: (
    textureOriginalUrl: string,
    isBump: boolean,
    isAlpha: boolean,
    canvasElement: HTMLCanvasElement,
  ) => void;
  transitionModelView: (
    regionData: RegionData,
    animateTransition?: boolean,
  ) => void;
}

const ModelViewerContext = createContext<ModelViewerProvider>(
  undefined as unknown as ModelViewerProvider,
);
export default () => useContext(ModelViewerContext);

export const ModelViewerProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {
    scene,
    camera,
    controls,
    renderer,
    render,
    constants,
    manager,
    animate,
    transitionObjectView,
  } = useModelViewer();

  const [loaded, setLoaded] = useState(false);

  /*   const webglAvailable = () => {
    try {
      const canvas = document.createElement("canvas");
      return !!(
        window.WebGLRenderingContext &&
        (canvas.getContext("webgl") || canvas.getContext("experimental-webgl"))
      );
    } catch (e) {
      return false;
    }
  }; */

  const getWidthHeightOfElement = (
    element: React.MutableRefObject<HTMLDivElement>,
  ) => {
    return new Vector2(
      element.current.clientWidth,
      element.current.clientHeight,
    );
  };

  const setupCameraAndLights = useCallback(
    (
      container: React.MutableRefObject<HTMLDivElement>,
      cameraPosition = { x: 0, y: 0, z: 40 },
    ) => {
      const ambientLight = new AmbientLight(0xffffff, 0.5);
      ambientLight.name = "light-1";
      scene.add(ambientLight);
      scene.add(camera);

      const directionalLight = new DirectionalLight(0xffffff, 0.4);
      directionalLight.position.set(0, 5, 30);
      directionalLight.name = "light-2";

      const pointLight = new PointLight(0xffffff, 0.2, 50);
      pointLight.position.set(10, 5, 45);
      scene.add(pointLight);

      const dimensions = getWidthHeightOfElement(container);
      camera.name = "PerspectiveCamera-1";
      camera.aspect = dimensions.x / dimensions.y;
      camera.position.set(cameraPosition.x, cameraPosition.y, cameraPosition.z);

      if (cameraPosition.z <= 0) {
        camera.position.z = 40;
      }

      camera.add(directionalLight);
      camera.updateProjectionMatrix();
    },
    [camera, scene],
  );

  const constrainToBoundaries = (
    value: number,
    upper: number,
    lower: number,
  ) => {
    if (value < upper && value > lower) {
      return value;
    }
    return value > lower ? upper : lower;
  };

  const setRenderSizeFromVector = useCallback(
    (renderer: WebGLRenderer, dimensions: Vector2) => {
      const y = constrainToBoundaries(
        dimensions.y,
        constants.yBoundaries.max,
        constants.yBoundaries.min,
      );
      const x = constrainToBoundaries(
        dimensions.x,
        constants.xBoundaries.max,
        constants.xBoundaries.min,
      );
      renderer.setSize(x, y);
    },
    [
      constants.xBoundaries.max,
      constants.xBoundaries.min,
      constants.yBoundaries.max,
      constants.yBoundaries.min,
    ],
  );

  const setRendererSizeFromContainer = useCallback(
    (
      renderer: WebGLRenderer,
      container: React.MutableRefObject<HTMLDivElement>,
    ) => {
      const dimensions = getWidthHeightOfElement(container);
      setRenderSizeFromVector(renderer, dimensions);
    },
    [setRenderSizeFromVector],
  );

  // @ts-ignore
  const notifySubscribers = (event) => {
    return event;
    // this.subscribers.forEach((fn) => fn(event));
  };

  const setupManager = useCallback(() => {
    manager.onProgress = (item, loaded, total) => {
      // ensure that we have actually loaded everything and then render
      if (loaded === total) {
        notifySubscribers({
          description: `loaded ${loaded} of ${total}`,
          status: "complete",
          finished: true,
        });

        render();
      } else {
        notifySubscribers({
          description: `loaded ${loaded} of ${total}`,
          status: "pending",
          finished: false,
        });
      }
    };
  }, [manager.onProgress, render]);

  const transitionModelView = useCallback(
    (regionData: RegionData, animateTransition = true) => {
      // TODO: We may need a check here for "loaded", but it seems to work fine without it...
      const movementData = JSON.parse(JSON.stringify(regionData.model));

      transitionObjectView(
        movementData.position,
        movementData.rotation,
        animateTransition,
        null,
      );
    },
    [transitionObjectView],
  );

  const loadProduct = useCallback(
    (modelData: ModelData, regionData: RegionData) => {
      const mtlLoader = new MTLLoader(manager);

      mtlLoader.setCrossOrigin("true");
      mtlLoader.setPath(modelData.path);
      mtlLoader.setMaterialOptions({
        ignoreZeroRGBs: true,
        side: DoubleSide,
      });

      mtlLoader.load(
        modelData.material,

        // @ts-ignore
        (materials) => {
          materials.preload();

          // @ts-ignore
          const loader = new OBJLoader(manager);
          loader.setPath(modelData.path);
          loader.setMaterials(materials);
          loader.load(
            modelData.model,
            // @ts-ignore
            (object) => {
              object.name = "mainProduct";

              scene.add(object);

              transitionModelView(regionData, false);
              animate();

              setLoaded(true);
            },

            // @ts-ignore
            manager.onProgress,
            manager.onError,
          );
        },
        null,
        null,
      );
    },
    [animate, manager, scene, transitionModelView],
  );

  const setupControls = useCallback(() => {
    controls.addEventListener("change", render);
    controls.maxDistance = constants.orbitMaxDistance;
    controls.minDistance = constants.orbitMinDistance;
    controls.enableDamping = true;
    controls.dampingFactor = 0.65;
  }, [
    constants.orbitMaxDistance,
    constants.orbitMinDistance,
    controls,
    render,
  ]);

  const handleResize = useCallback(
    (containerRef: React.MutableRefObject<HTMLDivElement>) => {
      const dimensions = getWidthHeightOfElement(containerRef);
      setRenderSizeFromVector(renderer, dimensions);
      camera.aspect =
        renderer.getSize(dimensions).width /
        renderer.getSize(dimensions).height;
      camera.updateProjectionMatrix();
      render();
    },
    [camera, render, renderer, setRenderSizeFromVector],
  );

  const setupEventListeners = useCallback(
    (containerRef: React.MutableRefObject<HTMLDivElement>) => {
      window.addEventListener(
        "resize",
        () => handleResize(containerRef),
        false,
      );
    },
    [handleResize],
  );

  const initScene = useCallback(
    (
      modelData: ModelData,
      containerRef: React.MutableRefObject<HTMLDivElement>,
      cameraPosition: Point,
      regionData: RegionData,
    ) => {
      setLoaded(false);

      if (containerRef === undefined) {
        return;
      }

      /*
      if (!webglAvailable()) {
        return;
      }
      */

      scene.clear();
      renderer.clear();
      camera.clear();

      renderer.setClearColor(0xffffff);
      renderer.setPixelRatio(window.devicePixelRatio);
      setRendererSizeFromContainer(renderer, containerRef);

      containerRef?.current.insertBefore(
        renderer.domElement,
        containerRef?.current.firstChild,
      );

      setupCameraAndLights(containerRef, cameraPosition);

      const myGroup = new Group();
      myGroup.name = "myGroup";
      scene.add(myGroup);

      setupManager();
      setupControls();
      setupEventListeners(containerRef);

      loadProduct(modelData, regionData);
    },
    [
      camera,
      loadProduct,
      renderer,
      scene,
      setRendererSizeFromContainer,
      setupCameraAndLights,
      setupControls,
      setupEventListeners,
      setupManager,
    ],
  );

  const beginRender = useCallback(() => {
    animate();
  }, [animate]);

  const setTexture = useCallback(
    (
      productModel: Object3D,
      texture: Texture,
      textureOriginalUrl: string,
      isBump: boolean,
      isAlpha: boolean,
    ) => {
      productModel?.traverse((child) => {
        const applyTexture = (
          material: Material,
          key: string,
          texture: Texture,
        ) => {
          material.dispose();
          // @ts-ignore
          material[key] = texture;
        };
        let key = "map";
        key = isBump ? "bumpMap" : key;
        key = isAlpha ? "alphaMap" : key;

        if (child instanceof Mesh) {
          const { material } = child;

          // TODO: Reevaluate material.materials
          // I don't think this check is valid anymore, but was in the old code.
          //  I'm leaving it until we have everything working
          if (material.materials) {
            material.materials
              // @ts-ignore
              .filter((m: Material) => m[key] !== undefined)
              .filter(
                (m: Material) =>
                  // @ts-ignore
                  m[key].originalUrl &&
                  // @ts-ignore
                  m[key].originalUrl === textureOriginalUrl,
              )
              .forEach((m: Material) => applyTexture(m, key, texture));
          } else if (
            isAlpha &&
            material[key] &&
            material[key].originalUrl &&
            material[key].originalUrl === textureOriginalUrl
          ) {
            applyTexture(material, key, texture);
          } else if (
            material.map &&
            material.map.originalUrl &&
            material.map.originalUrl === textureOriginalUrl
          ) {
            applyTexture(material, key, texture);
          }
        }
      });
    },
    [],
  );

  const loadTextureFromCanvas = useCallback(
    (
      textureOriginalUrl: string,
      isBump: boolean,
      isAlpha: boolean,
      canvasElement: HTMLCanvasElement,
    ) => {
      const texture = new Texture(canvasElement);
      texture.needsUpdate = true;
      // @ts-ignore
      texture.originalUrl = textureOriginalUrl;

      // TODO: Ensure that mainProduct is loaded before we ever try hitting this function
      const productModel = scene.getObjectByName("mainProduct");

      if (productModel) {
        setTexture(productModel, texture, textureOriginalUrl, isBump, isAlpha);
      }

      render();
    },
    [render, scene, setTexture],
  );

  const value = useMemo(
    () => ({
      initScene,
      beginRender,
      scene,
      camera,
      controls,
      renderer,
      manager,
      loaded,
      loadTextureFromCanvas,
      render,
      transitionModelView,
    }),
    [
      initScene,
      beginRender,
      scene,
      camera,
      controls,
      renderer,
      manager,
      loaded,
      loadTextureFromCanvas,
      render,
      transitionModelView,
    ],
  );

  return (
    <>
      {/* @ts-ignore */}
      <ModelViewerContext.Provider value={value}>
        {children}
      </ModelViewerContext.Provider>
    </>
  );
};
